import {
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type {
  AccessCatalog,
  SurfaceKey,
  TierKey,
} from "types/interfaces/access";
import { parentClosed, tierOn, type TierMap } from "./permissionsPanelModel";

const COLUMNS: TierKey[] = ["none", "view", "contribute", "manage"];

interface Props {
  catalog: AccessCatalog;
  value: TierMap;
  onChange: (surface: SurfaceKey, tier: TierKey) => void;
  disabled?: boolean;
}

/** One row per page; only the tiers a page supports can be picked, a page open
 * to every user stays at its one tier, and a sub-page waits for its page. */
export default function TierMatrix({
  catalog,
  value,
  onChange,
  disabled,
}: Props) {
  return (
    <Table size="small" sx={{ "& td, & th": { px: 1 } }}>
      <TableHead>
        <TableRow>
          <TableCell>Page</TableCell>
          {COLUMNS.map((tier) => (
            <TableCell key={tier} align="center">
              {catalog.tiers.find((t) => t.key === tier)?.label ?? tier}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {catalog.surfaces.map((surface) => {
          const current = tierOn(surface, value);
          const closedParent = parentClosed(catalog, value, surface);
          const supported = new Set<TierKey>([
            "none",
            ...surface.tiers.map((t) => t.tier),
          ]);
          return (
            <TableRow key={surface.key} hover>
              <TableCell sx={{ pl: surface.parent ? 3 : 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {surface.name}
                </Typography>
                {surface.everyone && (
                  <Typography variant="caption" color="text.secondary">
                    Open to every user
                  </Typography>
                )}
                {closedParent && (
                  <Typography variant="caption" color="text.secondary">
                    Needs {closedParent.name}
                  </Typography>
                )}
              </TableCell>
              {COLUMNS.map((tier) => (
                <TableCell key={tier} align="center">
                  {supported.has(tier) ? (
                    <Radio
                      size="small"
                      checked={current === tier}
                      disabled={
                        disabled ||
                        surface.everyone ||
                        (closedParent !== null && tier !== "none")
                      }
                      onChange={() => onChange(surface.key, tier)}
                      inputProps={{
                        "aria-label": `${surface.name}: ${tier}`,
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      -
                    </Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
